import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { NextServicio } from '../../modules/admin/services/services.types';
import { NextTaskType } from '../../core/task-type/task-type.types';
import { ServicesService } from '../../modules/admin/services/services.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'app-modify-task-type-dialog',
    templateUrl: './modify-task-type-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .modify-task-type-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModifyTaskTypeDialogComponent implements OnInit, OnDestroy {

    service: NextServicio;
    taskType: NextTaskType;
    dtypeForm: DTypeForm;
    hasChanges = false;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            taskType: NextTaskType;
            locator: NextLocator;
        },
        private dialogsService: DialogsService,
        public dialogRef: MatDialogRef<ModifyTaskTypeDialogComponent>,
        private servicesService: ServicesService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
    ) { }

    async ngOnInit() {
        this.taskType = this.data.taskType;
        this.dtypeForm = await this.servicesService.retrieveTaskTypeDtypeForm(this.taskType, this.data.locator, {isUpdate: true});
        this.dtypeForm.control.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this.hasChanges = true);
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    async modifyType() {
        const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('sendModifiedType'));
        if (confirm) {
            const form = this.dtypeForm.control;
            form.markAllAsTouched();
            if (form.valid) {
                this.dialogRef.close(form.getRawValue());
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.transloco.translate('errorsInForm')
                });
            }
        }
    }

    async closeDialog(result?: any) {
        if (this.hasChanges) {
            const confirm = await this.confirm(null, null, this.transloco.translate('exitWithoutSaving'));
            if (confirm) {
                this.dialogRef.close(result);
            }
        } else {
            this.dialogRef.close(result);
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

}
